import React, { forwardRef } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { iconBtnClass } from './iconBtnClass';

const FA_SIZE = { sm: 'sm', lg: 'lg' }; // md = undefined (FA default 16px)

const IconButton = forwardRef(({ tooltip, placement = 'top', disabled = false, size = 'md', className, badge, id, onClick, children, ...rest }, ref) => {
  const faSize = FA_SIZE[size];
  const icon = faSize ? React.cloneElement(children, { size: faSize }) : children;

  // 有 onClick 才是可互動元素，才給按鈕語意與鍵盤可及性。
  // 沒有 onClick 的（AppNavbar 的 chats、NotificationDropdown 的鈴鐺）是包在 Nav.Link /
  // NavDropdown title 裡的視覺容器，互動元素是外層那個 <a>；再給一次 role/tabIndex 會多出
  // 一個 tab 停留點，也造成互動元素巢狀
  const interactive = Boolean(onClick);
  const enabled = interactive && !disabled;

  // <span> 不像原生 button 會把 Enter / Space 轉成 click，要自己補。
  // Space 預設會捲動頁面，一律 preventDefault
  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    onClick(e);
  };

  // FontAwesome 的 svg 一律帶 aria-hidden（autoA11y 預設開啟），tooltip 又只在顯示時才存在
  // 於 portal，因此按鈕本身沒有可讀名稱；用已翻譯的 tooltip 補上。
  // 呼叫端一律要給 tooltip，沒給的話讀屏只會念「按鈕」
  const label = interactive ? tooltip : undefined;

  const inner = (
    <span
      // 展開在最前面：Dropdown.Toggle 注入的 aria-expanded 要進得來，但不能蓋掉下面那些
      // 由 IconButton 自己決定的屬性。也就是說 role / tabIndex / aria-label / aria-disabled /
      // onClick / onKeyDown / className 一律以 IconButton 的判斷為準，呼叫端傳進來會被忽略
      // （非互動時甚至會被覆寫成 undefined）
      {...rest}
      ref={ref}
      id={id}
      className={iconBtnClass({ size, disabled, className })}
      role={interactive ? 'button' : undefined}
      aria-label={label}
      tabIndex={interactive ? (disabled ? -1 : 0) : undefined}
      aria-disabled={interactive && disabled ? true : undefined}
      onClick={disabled ? undefined : onClick}
      onKeyDown={enabled ? handleKeyDown : undefined}
    >
      {icon}
      {/* 非互動時外層 <a> 才是互動元素，但 FA 的 svg 帶 aria-hidden，
          外層會變成沒有可讀名稱的空連結；把 tooltip 留一份給輔具讀 */}
      {!interactive && tooltip && <span className="visually-hidden">{tooltip}</span>}
      {badge}
    </span>
  );

  if (!tooltip) return inner;

  return (
    <OverlayTrigger placement={placement} overlay={<Tooltip>{tooltip}</Tooltip>}>
      {inner}
    </OverlayTrigger>
  );
});
IconButton.displayName = 'IconButton';

export default IconButton;
